import type { CostColumn, CostValues } from '../types'

export type ValueKey = keyof CostValues

/** Spec §1.2 order, columns 1–12 and the estimate before the forecast, with the potential changes after the pending contract changes (potential changes spec §3.4). */
export const VALUE_KEYS: ValueKey[] = [
  'originalBudget', 'transfers', 'approvedBudgetChanges', 'revisedBudget', 'pendingBudgetChanges', 'projectedBudget',
  'committed', 'approvedContractChanges', 'pendingContractChanges', 'potentialChanges', 'actual', 'estimate', 'projectedFinalCost', 'variance',
  'investorBilled', 'subcontractorBilled', 'billingDifference',
]

/** Shown only when the project has an investor contract (investor billing spec §6). */
export const BILLING_KEYS: ValueKey[] = ['investorBilled', 'subcontractorBilled', 'billingDifference']

export type ColumnId = 'code' | ValueKey
export const COLUMN_ORDER: ColumnId[] = ['code', ...VALUE_KEYS]

/** Spec §10: columns 1, 4, 7, 10, 11, 12 by default, plus the code and the potential changes (potential changes spec §3.4). */
export const DEFAULT_COLUMN_VISIBILITY: Record<ColumnId, boolean> = {
  code: true,
  originalBudget: true,
  transfers: false,
  approvedBudgetChanges: false,
  revisedBudget: true,
  pendingBudgetChanges: false,
  projectedBudget: false,
  committed: true,
  approvedContractChanges: false,
  pendingContractChanges: false,
  potentialChanges: true,
  actual: true,
  estimate: true,
  projectedFinalCost: true,
  variance: true,
  investorBilled: true,
  subcontractorBilled: true,
  billingDifference: true,
}

export const COLUMN_LABEL_KEY: Record<ColumnId, string> = {
  code: 'CostControl_col_code',
  originalBudget: 'CostControl_col_originalBudget',
  transfers: 'CostControl_col_transfers',
  approvedBudgetChanges: 'CostControl_col_approvedBudgetChanges',
  revisedBudget: 'CostControl_col_revisedBudget',
  pendingBudgetChanges: 'CostControl_col_pendingBudgetChanges',
  projectedBudget: 'CostControl_col_projectedBudget',
  committed: 'CostControl_col_committed',
  approvedContractChanges: 'CostControl_col_approvedContractChanges',
  pendingContractChanges: 'CostControl_col_pendingContractChanges',
  potentialChanges: 'CostControl_col_potentialChanges',
  actual: 'CostControl_col_actual',
  estimate: 'CostControl_col_estimate',
  projectedFinalCost: 'CostControl_col_projectedFinalCost',
  variance: 'CostControl_col_variance',
  investorBilled: 'CostControl_col_investorBilled',
  subcontractorBilled: 'CostControl_col_subcontractorBilled',
  billingDifference: 'CostControl_col_billingDifference',
}

export const NAME_WIDTH_CLASS = 'w-[22rem]'
export const COLUMN_WIDTH_CLASS: Record<ColumnId, string> = {
  code: 'w-28',
  originalBudget: 'w-36',
  transfers: 'w-36',
  approvedBudgetChanges: 'w-36',
  revisedBudget: 'w-36',
  pendingBudgetChanges: 'w-36',
  projectedBudget: 'w-36',
  committed: 'w-36',
  approvedContractChanges: 'w-36',
  pendingContractChanges: 'w-36',
  potentialChanges: 'w-36',
  actual: 'w-36',
  estimate: 'w-36',
  projectedFinalCost: 'w-36',
  variance: 'w-36',
  investorBilled: 'w-36',
  subcontractorBilled: 'w-36',
  billingDifference: 'w-36',
}

/** Base columns open the records behind the cell (`costBreakdown`). */
export const BASE_COLUMN: Partial<Record<ValueKey, CostColumn>> = {
  originalBudget: 'ORIGINAL_BUDGET',
  transfers: 'TRANSFERS',
  approvedBudgetChanges: 'APPROVED_BUDGET_CHANGES',
  pendingBudgetChanges: 'PENDING_BUDGET_CHANGES',
  committed: 'COMMITTED',
  approvedContractChanges: 'APPROVED_CONTRACT_CHANGES',
  pendingContractChanges: 'PENDING_CONTRACT_CHANGES',
  potentialChanges: 'POTENTIAL_CHANGES',
  actual: 'ACTUAL',
}

export interface FormulaTerm { key: ValueKey; sign: 1 | -1 }

/** Derived columns explain themselves from their parts (spec §1.2). */
export const FORMULA: Partial<Record<ValueKey, FormulaTerm[]>> = {
  revisedBudget: [{ key: 'originalBudget', sign: 1 }, { key: 'transfers', sign: 1 }, { key: 'approvedBudgetChanges', sign: 1 }],
  projectedBudget: [{ key: 'revisedBudget', sign: 1 }, { key: 'pendingBudgetChanges', sign: 1 }],
  variance: [{ key: 'revisedBudget', sign: 1 }, { key: 'projectedFinalCost', sign: -1 }],
}

export type DrillKind = 'records' | 'formula' | 'forecast' | 'none'
export const drillKind = (key: ValueKey): DrillKind => (BASE_COLUMN[key] ? 'records' : key === 'projectedFinalCost' ? 'forecast' : BILLING_KEYS.includes(key) ? 'none' : 'formula')

/** Every column the project can show: the billing ones only with an investor contract. */
export const availableColumns = (hasInvestorContract: boolean): ColumnId[] =>
  COLUMN_ORDER.filter((id) => hasInvestorContract || !BILLING_KEYS.includes(id as ValueKey))

export const visibleColumns = (visibility: Record<ColumnId, boolean>, hasInvestorContract: boolean): ColumnId[] =>
  availableColumns(hasInvestorContract).filter((id) => visibility[id])

/** A persisted visibility map from an older build may miss a column or carry junk: missing → default, unknown → dropped. */
export const sanitizeColumnVisibility = (raw: unknown): Record<ColumnId, boolean> => {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const out = { ...DEFAULT_COLUMN_VISIBILITY }
  COLUMN_ORDER.forEach((id) => {
    if (typeof src[id] === 'boolean') out[id] = src[id] as boolean
  })
  return out
}
